import { Loading, Tooltip } from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { useObjectPermissionOnlineOnlyState } from '@affine/core/modules/permissions/utils/object-permission-ui';
import * as reviewStyles from '@affine/core/modules/permissions/views/permission-review-list.css';
import { PermissionExpandableSection } from '@affine/core/modules/permissions/views/permission-section';
import { ShareInfoService } from '@affine/core/modules/share-doc';
import type { WorkspaceMetadata } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import type { Store } from '@blocksuite/affine/store';
import { ShareIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { type ComponentProps, forwardRef, type Ref, useEffect } from 'react';

import { DocPermissionSections } from './doc-permission-sections';
import { ExternalAccessSection } from './external-access-section';
import * as styles from './index.css';
import { ShareExport } from './share-export';
import { ShareLoadNotice, SharePage } from './share-page';
import { useDocShareRestrictions } from './use-doc-share-restrictions';

export interface ShareMenuProps {
  workspaceMetadata: WorkspaceMetadata;
  currentPage: Store;
  onEnableAffineCloud: () => void;
  folderId?: string;
  hideExport?: boolean;
  hideGuests?: boolean;
  hideLinkSharing?: boolean;
}

export const ShareMenuContent = (props: ShareMenuProps) => {
  const t = useI18n();
  const { hideExport, hideLinkSharing } = props;
  const { isReady, privateFolderDocShareBlocked, timedOut, retry } =
    useDocShareRestrictions();
  const online = useObjectPermissionOnlineOnlyState();

  if (props.workspaceMetadata.flavour === 'local') {
    return (
      <div className={styles.containerStyle} data-ui-area="share">
        {hideLinkSharing ? null : <SharePage {...props} />}
      </div>
    );
  }

  return (
    <div
      className={styles.containerStyle}
      data-ui-area="share"
      aria-busy={!isReady}
    >
      {!isReady ? (
        timedOut || online.offline ? (
          <ShareLoadNotice offline={online.offline} onRetry={retry} />
        ) : (
          <div className={reviewStyles.notice} role="status">
            <Loading size={14} />
            {t['com.streamlain.permissions.review.loading']()}
          </div>
        )
      ) : null}
      {privateFolderDocShareBlocked ? (
        <div role="status" className={styles.privateFolderWarning}>
          {t[
            'com.streamlain.share-menu.invite-editor.private-folder-doc-disabled'
          ]()}
        </div>
      ) : null}
      <DocPermissionSections
        disabled={!isReady || privateFolderDocShareBlocked}
      />
      {hideLinkSharing ? null : (
        <ExternalAccessSection
          workspaceId={props.workspaceMetadata.id}
          docId={props.currentPage.id}
          disabled={!isReady || privateFolderDocShareBlocked}
        />
      )}
      {!hideExport && !BUILD_CONFIG.isMobileEdition ? (
        <PermissionExpandableSection title={t['Export']()} defaultCollapsed>
          <ShareExport />
        </PermissionExpandableSection>
      ) : null}
    </div>
  );
};

export const DefaultShareButton = forwardRef(function DefaultShareButton(
  props: ComponentProps<typeof Button>,
  ref: Ref<HTMLButtonElement>
) {
  const t = useI18n();
  const shareInfoService = useService(ShareInfoService);
  const shared = useLiveData(shareInfoService.shareInfo.isShared$);

  useEffect(() => {
    shareInfoService.shareInfo.revalidate();
  }, [shareInfoService]);

  return (
    <Tooltip
      content={
        shared
          ? t['com.streamlain.share-menu.option.link.readonly.description']()
          : t['com.streamlain.share-menu.option.link.no-access.description']()
      }
    >
      <Button {...props} ref={ref} className={styles.button} variant="primary">
        <div className={styles.buttonContainer}>
          <ShareIcon fontSize={16} />
          {t['com.streamlain.share-menu.shareButton']()}
        </div>
      </Button>
    </Tooltip>
  );
});
